import { faCheck, faLockKeyhole } from '@fortawesome/pro-solid-svg-icons';
import type { CSSProperties, ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import styled, { css } from 'styled-components';

import { Button } from 'atoms/buttons/Button';
import { FontAwesomeIcon } from 'atoms/icons';
import { Drawer } from 'atoms/layout/Drawer';
import { Horizontal, Vertical } from 'atoms/layout/flex';
import { LabelL, LabelS } from 'atoms/typography';
import { Chip } from 'atoms/ui/Chip';
import { Dialog } from 'components/dialog';
import type { ConfigQuery } from 'contexts/config/types';
import { useIsLaptop } from 'hooks/device/useIsLaptop';
import { useAmountWithConversion } from 'hooks/useAmountWithConversion';
import { useMonetaryAmount } from 'hooks/useMonetaryAmount';
import { glossary } from 'lib/glossary';
import { isType } from 'lib/gql';
import { MonetaryAmount } from 'lib/monetaryAmount';
import { getFormattedSport } from 'lib/sports';

import { Cta, Label } from '../Cta';
import { Remaining } from '../Remaining';
import cover from './assets/cover.webp';
import creditCoin from './assets/credit-coin.webp';
import boxImg from './assets/reward.webp';

const Line = styled.div<{ active: boolean }>`
  position: absolute;
  width: var(--unit);
  top: calc(var(--size) - 1px);
  bottom: -1px;
  left: calc(var(--size) / 2);
  transform: translateX(-50%);
  background-color: var(--c-nl-600-opaque);

  ${({ active }) =>
    active &&
    css`
      background-color: var(--c-white);
    `}
`;

const StepRoot = styled(Horizontal).attrs({ gap: 1.5 })`
  width: 100%;
  align-items: flex-start;
  position: relative;
  min-height: 50px;
  padding-bottom: var(--triple-unit);
  --size: var(--triple-unit);

  &:last-child {
    padding-bottom: 0;
    ${Line} {
      display: none;
    }
  }
`;

const Pin = styled(Horizontal).attrs({ gap: 0 })<{ active: boolean }>`
  justify-content: center;
  background-color: var(--c-nl-600-opaque);
  width: var(--size);
  height: var(--size);
  flex-shrink: 0;
  z-index: 1;
  border-radius: 50%;
  color: var(--c-blue-400);
  ${({ active }) =>
    active &&
    css`
      background-color: var(--c-white);
    `}
`;

const Root = styled(Vertical).attrs({ gap: 1 })`
  padding: var(--double-unit);
  background: linear-gradient(
    84.1deg,
    #3c3639 0%,
    #34323e 28.32%,
    #31353f 54.01%,
    #1e2b3f 100%
  );
`;

const BoxImg = styled.img`
  align-self: flex-start;
`;

const Step = ({
  index,
  state,
  children,
}: {
  index: number;
  state: 'init' | 'inprogress' | 'completed';
  children: ReactNode;
}) => {
  const renderLabel = () => {
    if (state === 'completed') {
      return <FontAwesomeIcon icon={faCheck} size="xs" />;
    }
    if (state === 'inprogress') {
      return <FontAwesomeIcon icon={faLockKeyhole} size="xs" />;
    }
    return (
      <LabelL color="var(--c-white)" bold>
        {index}
      </LabelL>
    );
  };
  return (
    <StepRoot>
      <Pin active={['inprogress', 'completed'].includes(state)}>
        {renderLabel()}
      </Pin>
      <Line active={state === 'completed'} />
      {children}
    </StepRoot>
  );
};

type DetailsContentProps = {
  event: NonNullable<ConfigQuery['currentUser']>['completablePromotionalEvent'];
};

const Header = styled.div`
  background-image: var(--background-image, url(${cover}));
  width: 100%;
  aspect-ratio: 6 / 2;
  background-position: center top;
  background-size: cover;
`;

export const useCCRewardRange = () => {
  const { toMonetaryAmount } = useMonetaryAmount();

  return (
    probableRewardConfigs: NonNullable<
      NonNullable<ConfigQuery['currentUser']>['completablePromotionalEvent']
    >['probableRewardConfigs']
  ) => {
    const allConversionCreditAmounts = probableRewardConfigs
      .flatMap(bundle => bundle.rewardConfigs)
      .filter(
        rewardConfig =>
          rewardConfig.__typename === 'ProbabilisticBundleRewardConfig'
      )
      .flatMap(bundle => bundle.probabilisticBundleConfig.slots)
      .flatMap(slot => slot.probableConfigs)
      .flatMap(probableConfig => probableConfig.conversionCredit?.maxDiscount)
      .filter(Boolean)
      .map(toMonetaryAmount);

    if (!allConversionCreditAmounts.length) {
      return undefined;
    }
    const { max, min } = allConversionCreditAmounts.reduce<{
      max: MonetaryAmount;
      min: MonetaryAmount;
    }>(
      (acc, value) => ({
        max:
          Number(acc.max.referenceValue) > Number(value.referenceValue)
            ? acc.max
            : value,
        min:
          Number(acc.min.referenceValue) < Number(value.referenceValue)
            ? acc.min
            : value,
      }),
      { max: allConversionCreditAmounts[0], min: allConversionCreditAmounts[0] }
    );
    return { max, min };
  };
};

const RewardRange = ({
  min,
  max,
}: {
  min: MonetaryAmount;
  max: MonetaryAmount;
}) => {
  const { main } = useAmountWithConversion({ monetaryAmount: min });
  const { main: mainMax } = useAmountWithConversion({ monetaryAmount: max });
  return (
    <FormattedMessage
      id="AddFundsToFiatWallet.Promo.Details.MysteryJersey"
      defaultMessage="{min} to {max} in credits"
      values={{
        min: main,
        max: mainMax,
      }}
    />
  );
};

const DetailsContent = ({ event }: DetailsContentProps) => {
  const { toMonetaryAmount } = useMonetaryAmount();

  const getCCRewardRange = useCCRewardRange();

  const { main } = useAmountWithConversion({
    monetaryAmount: event?.completionAmount || MonetaryAmount.zero,
  });

  if (!event) return null;

  const ccRange = getCCRewardRange(event?.probableRewardConfigs);
  const hasDeposit = !!event?.promotionalEventCompletion;

  const { sport } = event;

  const formattedSport = sport ? getFormattedSport(sport) : undefined;

  const minimumDepositAmount =
    (event.eligibilityRules.minimumDepositAmount || 0) / 100;

  const completionPercentage = event.completionRules.completion_percentage;

  const { completionAmount } = event;
  const completionMonetaryAmount = toMonetaryAmount(completionAmount);

  const hasMaximumNumberOfParticipants =
    isType(event, 'DepositPromotionalEvent') &&
    !!event.completionRules.maximum_number_of_participants;

  return (
    <Vertical gap={0}>
      <Header
        style={
          {
            '--background-image': event.promotionDetailsRewardPictureUrl
              ? `url(${event.promotionDetailsRewardPictureUrl})`
              : undefined,
          } as CSSProperties
        }
      />
      <Root gap={1}>
        <div>
          <Step index={1} state={hasDeposit ? 'completed' : 'inprogress'}>
            <Vertical grow>
              <LabelL bold>
                <Label minimumAmount={minimumDepositAmount} />
              </LabelL>
              <Cta />
            </Vertical>
          </Step>
          <Step
            index={2}
            state={event.promotionalEventCompletion ? 'inprogress' : 'init'}
          >
            {!!completionPercentage && (
              <LabelL bold>
                <FormattedMessage
                  id="AddFundsToFiatWallet.Promo.Details.spendHalf2"
                  defaultMessage="Spend at least {percentage, number, ::percent} of your deposit in {sport, select, undefined {} other {{sport} }}Instant Buy or Auctions"
                  values={{
                    percentage: completionPercentage / 100,
                    sport: formattedSport,
                  }}
                />
              </LabelL>
            )}
            {!!completionAmount && !completionPercentage && (
              <LabelL bold>
                {completionMonetaryAmount.isZero ? (
                  <FormattedMessage
                    id="AddFundsToFiatWallet.Promo.Details.spendMinimumAmount.isZero"
                    defaultMessage="Buy a card in {sport, select, undefined {} other {{sport} }} on Instant Buy or Auctions"
                    values={{
                      value: main,
                      sport: formattedSport,
                    }}
                  />
                ) : (
                  <FormattedMessage
                    id="AddFundsToFiatWallet.Promo.Details.spendMinimumAmount"
                    defaultMessage="Spend at least {value} of your deposit in {sport, select, undefined {} other {{sport} }}Instant Buy or Auctions"
                    values={{
                      sport: formattedSport,
                    }}
                  />
                )}
              </LabelL>
            )}
          </Step>
          <Step index={3} state="init">
            <Vertical>
              {hasMaximumNumberOfParticipants ? (
                <Remaining
                  completedParticipants={event.completedParticipants}
                  maximumNumberOfParticipants={
                    event.completionRules.maximum_number_of_participants
                  }
                />
              ) : (
                <LabelL bold>
                  <FormattedMessage
                    id="AddFundsToFiatWallet.Promo.Details.remaining.default"
                    defaultMessage="Earn wheel tickets!"
                  />
                </LabelL>
              )}
              {ccRange?.min !== ccRange?.max ? (
                <Horizontal>
                  <BoxImg alt="" src={boxImg} width="40px" height="40px" />
                  <Vertical>
                    <LabelL bold>
                      <FormattedMessage
                        id="AddFundsToFiatWallet.Promo.Details.winRewardBox"
                        defaultMessage="Win Reward Box"
                      />
                    </LabelL>
                    <Chip size="medium">
                      <Vertical>
                        {!!ccRange && (
                          <Horizontal>
                            <img
                              alt=""
                              src={creditCoin}
                              width="20px"
                              height="20px"
                            />
                            <LabelS bold>
                              <RewardRange {...ccRange} />
                            </LabelS>
                          </Horizontal>
                        )}
                      </Vertical>
                    </Chip>
                  </Vertical>
                </Horizontal>
              ) : (
                <Horizontal>
                  <LabelL bold>
                    {event.promotionDetailsRewardDescription}
                  </LabelL>
                </Horizontal>
              )}
            </Vertical>
          </Step>
        </div>
        {!!event.blogUrl && (
          <Button
            externalLink
            size="medium"
            color="secondary"
            href={event.blogUrl}
          >
            <FormattedMessage {...glossary.learnMore} />
          </Button>
        )}
      </Root>
    </Vertical>
  );
};

export const Details = ({
  open,
  onClose,
  ...props
}: {
  open: boolean;
  onClose: () => void;
} & Pick<DetailsContentProps, 'event'>) => {
  const isTablet = useIsLaptop();
  if (isTablet) {
    return (
      <Dialog hideHeader maxWidth="xs" open={open} onClose={onClose}>
        <DetailsContent {...props} />
      </Dialog>
    );
  }
  return (
    <Drawer open={open} onBackdropClick={onClose} side="bottom">
      <DetailsContent {...props} />
    </Drawer>
  );
};
